import { baseQuery } from "./baseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";
import { UserChangePassword } from "shared/models";

export const userService = createApi({
  reducerPath: "userService",
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getUserInfo: builder.query<any, void>({
      query: (params) => ({
        url: `users/info`,
        method: "GET",
        headers: {
          PageName: "User Management",
        },
      }),
    }),
    changePassword: builder.mutation<any, UserChangePassword>({
      query: ({ id, ...others }) => ({
        url: `/users/${id}/change-password`,
        body: others,
        method: "PUT",
        headers: {
          PageName: "Profile",
        },
      }),
    }),
    getUsers: builder.query<
      any,
      {
        page: number;
        size: number;
        search: string;
        role_id: any;
        status: number[];
        role: any;
        statusUser: any;
      }
    >({
      query: (params) => ({
        url: `/users`,
        params: { ...params, order_by: "created_at", direction: "desc" },
        method: "GET",
        headers: {
          PageName: "User Management",
        },
      }),
    }),
    getUsersSelect: builder.query<
      any,
      {
        page: number;
        size: number;
        filter_api_permission?: boolean;
      }
    >({
      query: (params) => ({
        url: `/users`,
        params: { ...params, order_by: "email", direction: "asc" },
        method: "GET",
        headers: {
          PageName: "User Management",
        },
      }),
    }),
    getUserDetail: builder.query<any, { id: string }>({
      query: (params) => ({
        url: `/users/${params?.id}`,
        method: "GET",
        headers: {
          PageName: "User Management",
        },
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success) {
          return rawData.data;
        }
      },
    }),
    getViewKeyAndWhitelistIp: builder.query<any, { id: string }>({
      query: (params) => ({
        url: `/users/${params?.id}/whiteListIp`,
        method: "GET",
        headers: {
          PageName: "User Management",
        },
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success) {
          return rawData.data;
        }
      },
    }),
    createUser: builder.mutation<any, any>({
      query: (body: any) => ({
        url: "/users",
        body,
        method: "POST",
        headers: {
          PageName: "User Management",
        },
      }),
    }),
    updateUser: builder.mutation<any, any>({
      query: (body: any) => ({
        url: `/users/${body.id}`,
        body,
        method: "PUT",
        headers: {
          PageName: "User Management",
        },
      }),
      transformErrorResponse: (error) => {
        return error;
      },
    }),
    updateUserActivate: builder.mutation<any, any>({
      query: (body: any) => ({
        url: `/users/activate/${body.id}`,
        body,
        method: "PUT",
        headers: {
          PageName: "User Management",
        },
      }),
    }),
    updateUserDeactivate: builder.mutation<any, any>({
      query: (body: any) => ({
        url: `/users/deactivate/${body.id}`,
        body,
        headers: {
          PageName: "User Management",
        },
        method: "PUT",
      }),
    }),
    deleteUser: builder.mutation<any, any>({
      query: (body: any) => ({
        url: `/users/${body.id}`,
        body,
        method: "DELETE",
        headers: {
          PageName: "User Management",
        },
      }),
    }),
    resetPasswordUser: builder.mutation<any, any>({
      query: (body: any) => ({
        url: `/users/${body.id}/reset-password`,
        body,
        method: "PUT",
        headers: {
          PageName: "User Management",
        },
      }),
    }),
    getWhiteList: builder.query<any, void>({
      query: () => ({
        url: `/users/whitelistedips/list`,
        method: "GET",
        headers: {
          PageName: "Whitelisted-Profile",
        },
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success) {
          return rawData.data;
        }
      },
    }),
    createWhiteList: builder.mutation<any, any>({
      query: (body) => ({
        url: `/users/whitelistedips`,
        body,
        method: "POST",
        headers: {
          PageName: "Whitelisted-Profile",
        },
      }),
    }),
    updateWhiteList: builder.mutation<any, any>({
      query: (body) => ({
        url: `/users/whitelistedips/${body.id}`,
        body,
        method: "PUT",
        headers: {
          PageName: "Whitelisted-Profile",
        },
      }),
    }),
    deleteWhiteList: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/users/whitelistedips/${id}`,
        method: "DELETE",
        headers: {
          PageName: "Whitelisted-Profile",
        },
      }),
    }),
    resetSecretKey: builder.mutation<any, void>({
      query: () => ({
        url: `/users/reset-secret-key`,
        method: "PATCH",
        headers: {
          PageName: "Profile",
        },
      }),
    }),
    resetPublicKey: builder.mutation<any, void>({
      query: () => ({
        url: `/users/reset-public-key`,
        method: "PATCH",
        headers: {
          PageName: "Profile",
        },
      }),
    }),
    setupAuthenticatorMode: builder.mutation<any, { mode: number }>({
      query: (params) => ({
        url: `/users/setup-authenticator-mode/${params?.mode}`,
        method: "POST",
        headers: {
          PageName: "Profile",
        },
      }),
    }),
    removeAuthenticatorMode: builder.mutation<any, { mode: number }>({
      query: (params) => ({
        url: `/users/remove-authenticator-mode/${params?.mode}`,
        method: "DELETE",
        headers: {
          PageName: "Profile",
        },
      }),
    }),
    reset2Fa: builder.mutation<any, { id: number }>({
      query: (params) => ({
        url: `/users/reset-authenticator-mode/${params?.id}`,
        method: "PUT",
        headers: {
          PageName: "User Management",
        },
      }),
    }),
    getWhiteListedIp: builder.query<any, any>({
      query: (body) => ({
        url: `/users/whiteListIp/${body.id}`,
        method: "GET",
        headers: {
          PageName: "User Management",
        },
      }),
    }),
  }),
});

export const {
  useLazyGetUserDetailQuery,
  useGetUserInfoQuery,
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetUsersSelectQuery,
  useLazyGetUserInfoQuery,
  useCreateUserMutation,
  useDeleteUserMutation,
  useUpdateUserMutation,
  useUpdateUserActivateMutation,
  useUpdateUserDeactivateMutation,
  useChangePasswordMutation,
  useResetPasswordUserMutation,
  useGetWhiteListQuery,
  useCreateWhiteListMutation,
  useUpdateWhiteListMutation,
  useDeleteWhiteListMutation,
  useResetPublicKeyMutation,
  useResetSecretKeyMutation,
  useSetupAuthenticatorModeMutation,
  useRemoveAuthenticatorModeMutation,
  useReset2FaMutation,
  useLazyGetViewKeyAndWhitelistIpQuery,
} = userService;
